package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.core.util.spreadsheet.isSpreadsheetImportSupported
import br.com.vvrefeicoes.portal.core.util.spreadsheet.pickPurchasesSpreadsheet
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportMapping
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnitSuggestion
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPayload
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPreview
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.CircleCheck
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.TriangleAlert
import com.composables.icons.lucide.Upload
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_extra_import_count
import portalvv.shared.generated.resources.compras_extra_import_warning
import portalvv.shared.generated.resources.compras_import_action
import portalvv.shared.generated.resources.compras_import_category_label
import portalvv.shared.generated.resources.compras_import_error
import portalvv.shared.generated.resources.compras_import_format
import portalvv.shared.generated.resources.compras_import_items
import portalvv.shared.generated.resources.compras_import_period
import portalvv.shared.generated.resources.compras_import_processing
import portalvv.shared.generated.resources.compras_import_ready
import portalvv.shared.generated.resources.compras_import_remove_file
import portalvv.shared.generated.resources.compras_import_select_file
import portalvv.shared.generated.resources.compras_import_sheet
import portalvv.shared.generated.resources.compras_import_step_category
import portalvv.shared.generated.resources.compras_import_step_upload
import portalvv.shared.generated.resources.compras_import_total
import portalvv.shared.generated.resources.compras_import_unsupported
import portalvv.shared.generated.resources.compras_import_warning_middle
import portalvv.shared.generated.resources.compras_import_warning_prefix
import portalvv.shared.generated.resources.compras_import_warning_title

@Composable
fun PurchasesImportDialog(
    period: MonthPeriod,
    initialCategory: PurchasesCategory,
    controller: PurchasesEntriesController,
    extraController: PurchasesExtraOrdersController,
    onDismiss: () -> Unit,
    onImported: () -> Unit,
) {
    var selectedCategory by remember { mutableStateOf(initialCategory) }
    var fileName by remember { mutableStateOf<String?>(null) }
    var payload by remember { mutableStateOf<PurchasesImportPayload?>(null) }
    var extrasOrders by remember { mutableStateOf<List<PurchasesExtraImportOrder>>(emptyList()) }
    var extrasGroups by remember { mutableStateOf<List<PurchasesExtraUnitGroup>>(emptyList()) }
    var extrasSuggestions by remember { mutableStateOf<List<PurchasesExtraUnitSuggestion>>(emptyList()) }
    var extrasMappings by remember { mutableStateOf<Map<String, String?>>(emptyMap()) }
    var showUnitReview by remember { mutableStateOf(false) }
    var localError by remember { mutableStateOf<String?>(null) }
    var picking by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val supported = isSpreadsheetImportSupported()
    val preview = controller.importPreview
    val extraPreview = extraController.importPreview
    val periodLabel = MonthCalendar.label(period)
    val pickErrorMessage = stringResource(Res.string.compras_import_error)
    val error =
        localError
            ?: if (selectedCategory == PurchasesCategory.Extras) {
                extraController.importError
            } else {
                controller.importError
            }

    LaunchedEffect(Unit) {
        controller.resetImport()
        extraController.resetImport()
    }

    fun clearFile() {
        fileName = null
        payload = null
        extrasOrders = emptyList()
        extrasGroups = emptyList()
        extrasSuggestions = emptyList()
        extrasMappings = emptyMap()
        showUnitReview = false
        localError = null
        controller.resetImport()
        extraController.resetImport()
    }

    fun selectFile() {
        if (!supported || picking || controller.previewing || extraController.importing) return
        scope.launch {
            picking = true
            clearFile()
            try {
                if (selectedCategory == PurchasesCategory.Extras) {
                    val picked = pickPurchasesSpreadsheet(null)
                    if (picked != null) {
                        val parsed = parsePurchasesExtraOrders(picked.rows)
                        fileName = picked.fileName
                        extrasOrders = parsed.orders
                        extrasGroups = parsed.unitGroups
                        extraController.loadUnits()
                        val suggestions = extraController.previewUnits(parsed.unitGroups.map { it.label })
                        extrasSuggestions = suggestions
                        extrasMappings = suggestions.associate { it.label to it.unitId }
                        showUnitReview = true
                    }
                } else {
                    val picked = pickPurchasesSpreadsheet("fech")
                    if (picked != null) {
                        val type = selectedCategory.type ?: return@launch
                        val parsed = parsePurchasesSpreadsheet(picked.rows, type, period.year, period.month)
                        fileName = picked.fileName
                        payload = parsed
                        controller.previewImport(parsed)
                    }
                }
            } catch (exception: Throwable) {
                localError = exception.message ?: pickErrorMessage
            } finally {
                picking = false
            }
        }
    }

    AppModalCard(
        title = stringResource(Res.string.compras_import_sheet),
        onDismiss = onDismiss,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .widthIn(max = 460.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            Text(
                text = stringResource(Res.string.compras_import_period, periodLabel),
                style = MaterialTheme.typography.bodyMedium,
            )

            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                StepHeader(
                    number = 1,
                    title = stringResource(Res.string.compras_import_step_category),
                )
                CategoryDropdown(
                    selected = selectedCategory,
                    options = PurchasesCategory.entries,
                    onSelect = { category ->
                        selectedCategory = category
                        clearFile()
                    },
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                StepHeader(
                    number = 2,
                    title = stringResource(Res.string.compras_import_step_upload),
                )
                AppButton(
                    text = stringResource(Res.string.compras_import_select_file),
                    onClick = ::selectFile,
                    variant = PortalButtonVariant.Secondary,
                    leadingIcon = Lucide.Upload,
                    enabled = supported && !picking && !controller.previewing && !extraController.importing,
                )
                Text(
                    text = stringResource(Res.string.compras_import_format),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (!supported) {
                    Text(
                        text = stringResource(Res.string.compras_import_unsupported),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            error?.let { message ->
                Text(
                    text = message,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error,
                )
            }

            if (fileName != null) {
                ImportedFileCard(
                    fileName = fileName.orEmpty(),
                    onRemove = ::clearFile,
                )
                if (picking) {
                    Text(
                        text = stringResource(Res.string.compras_import_processing),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                } else if (selectedCategory == PurchasesCategory.Extras) {
                    if (extraPreview != null) {
                        ReadyForImport()
                        Text(
                            text = stringResource(Res.string.compras_extra_import_count, extraPreview.orderCount),
                            style = MaterialTheme.typography.bodyMedium,
                        )
                        if (extraPreview.willReplace) {
                            Row(
                                verticalAlignment = Alignment.Top,
                                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                            ) {
                                Icon(
                                    imageVector = Lucide.TriangleAlert,
                                    contentDescription = null,
                                    tint = AppPalette.Warning500,
                                    modifier = Modifier.size(24.dp),
                                )
                                Text(
                                    text = stringResource(Res.string.compras_extra_import_warning, periodLabel),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurface,
                                )
                            }
                        }
                    }
                } else if (preview != null) {
                    ReadyForImport()
                    ImportedTotals(preview = preview)
                    if (preview.yearHasEntries) {
                        ImportWarning(
                            category = selectedCategory,
                            periodLabel = periodLabel,
                        )
                    }
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_import_action),
                    onClick = {
                        if (selectedCategory == PurchasesCategory.Extras) {
                            if (extrasOrders.isNotEmpty()) {
                                val mappings =
                                    extrasMappings.map { (label, unitId) ->
                                        PurchasesExtraImportMapping(label, unitId)
                                    }
                                scope.launch {
                                    extraController.importOrders(period, extrasOrders, mappings)?.let {
                                        onImported()
                                    }
                                }
                            }
                        } else {
                            payload?.let { currentPayload ->
                                scope.launch {
                                    controller.importEntries(currentPayload)?.let { onImported() }
                                }
                            }
                        }
                    },
                    enabled =
                        if (selectedCategory == PurchasesCategory.Extras) {
                            fileName != null &&
                                extrasOrders.isNotEmpty() &&
                                extraPreview != null &&
                                !picking &&
                                !extraController.importing
                        } else {
                            fileName != null &&
                                payload != null &&
                                preview?.canImport == true &&
                                !picking &&
                                !controller.importing
                        },
                )
            }
        }
    }

    if (showUnitReview) {
        PurchasesExtraUnitReviewDialog(
            suggestions = extrasSuggestions,
            units = extraController.units,
            orderCounts = extrasGroups.associate { it.label to it.count },
            mappings = extrasMappings,
            saving = false,
            onSelect = { label, unitId ->
                extrasMappings = extrasMappings + (label to unitId)
            },
            onConfirm = {
                showUnitReview = false
                scope.launch {
                    extraController.previewImport(period, extrasOrders)
                }
            },
            onDismiss = { showUnitReview = false },
        )
    }
}

@Composable
private fun StepHeader(
    number: Int,
    title: String,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
    ) {
        Box(
            modifier =
                Modifier
                    .size(24.dp)
                    .background(MaterialTheme.colorScheme.primary, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = number.toString(),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onPrimary,
            )
        }
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
        )
    }
}

@Composable
private fun CategoryDropdown(
    selected: PurchasesCategory,
    options: List<PurchasesCategory>,
    onSelect: (PurchasesCategory) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = stringResource(Res.string.compras_import_category_label),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Box {
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .clickable { expanded = true }
                        .padding(vertical = AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(selected.label),
                    style = MaterialTheme.typography.bodyLarge,
                    modifier = Modifier.weight(1f),
                )
                Icon(
                    imageVector = Lucide.ChevronDown,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                modifier = Modifier.widthIn(min = 260.dp),
            ) {
                options.forEach { category ->
                    DropdownMenuItem(
                        text = { Text(stringResource(category.label)) },
                        onClick = {
                            onSelect(category)
                            expanded = false
                        },
                    )
                }
            }
        }
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
    }
}

@Composable
private fun ImportedFileCard(
    fileName: String,
    onRemove: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        color = MaterialTheme.colorScheme.surface,
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(start = AppSpacing.space12, end = AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Icon(
                imageVector = Lucide.FileText,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = fileName,
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            IconButton(onClick = onRemove) {
                Icon(
                    imageVector = Lucide.Trash2,
                    contentDescription = stringResource(Res.string.compras_import_remove_file),
                    tint = MaterialTheme.colorScheme.error,
                )
            }
        }
    }
}

@Composable
private fun ReadyForImport() {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Icon(
            imageVector = Lucide.CircleCheck,
            contentDescription = null,
            tint = AppPalette.Success500,
            modifier = Modifier.size(18.dp),
        )
        Text(
            text = stringResource(Res.string.compras_import_ready),
            style = MaterialTheme.typography.bodySmall,
            color = AppPalette.Success900,
        )
    }
}

@Composable
private fun ImportedTotals(preview: PurchasesImportPreview) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        TotalRow(
            label = stringResource(Res.string.compras_import_items),
            value = preview.productCount.toString(),
        )
        TotalRow(
            label = stringResource(Res.string.compras_import_total),
            value = "R$ ${MoneyFormatter.formatCents(preview.totalCents)}",
        )
    }
}

@Composable
private fun TotalRow(
    label: String,
    value: String,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
        )
    }
}

@Composable
private fun ImportWarning(
    category: PurchasesCategory,
    periodLabel: String,
) {
    val warningTitle = stringResource(Res.string.compras_import_warning_title)
    val warningPrefix = stringResource(Res.string.compras_import_warning_prefix)
    val warningMiddle = stringResource(Res.string.compras_import_warning_middle)
    val categoryLabel = stringResource(category.label)
    val warning =
        buildAnnotatedString {
            withStyle(SpanStyle(fontWeight = FontWeight.SemiBold)) {
                append(warningTitle)
            }
            append(" ")
            append(warningPrefix)
            append(" ")
            withStyle(SpanStyle(fontWeight = FontWeight.SemiBold)) {
                append(categoryLabel)
            }
            append(" ")
            append(warningMiddle)
            append(" ")
            withStyle(SpanStyle(fontWeight = FontWeight.SemiBold)) {
                append(periodLabel)
            }
            append(".")
        }

    Row(
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
    ) {
        Icon(
            imageVector = Lucide.TriangleAlert,
            contentDescription = null,
            tint = AppPalette.Warning500,
            modifier = Modifier.size(24.dp),
        )
        Text(
            text = warning,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}
